import React, { useEffect, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Coins, plus } from '../../assets'
import { GA_EVENTS } from '../../utils/gaConstants';
import { useTrackEvent } from '../../utils/analyticEvents';

const slideUpFadeIn = keyframes`
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const fadeOutSlideDown = keyframes`
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(10px);
    opacity: 0;
  }
`;

const SnackBarWrapper = styled.div`
  pointer-events: auto;
  animation: ${props => props.isExiting ? css`${fadeOutSlideDown} 0.3s forwards` : css`${slideUpFadeIn} 0.4s ease-out`};
  min-width: 300px;
  max-width: 90vw;
`;

const SnackBar = ({
  id,
  message,
  text,
  earnedCoins,
  coinValue,
  action,
  icon,
  icon_url,
  primary_url,
  content,
  duration = 2000,
  type = 'info',
  onClose
}) => {
  const [isExiting, setIsExiting] = useState(false);
  const trackEvent = useTrackEvent();
  
  const isReward = action === 'reward_alert' || type === 'reward' || !!earnedCoins || !!coinValue;
  const displayCoins = coinValue || earnedCoins;
  const displayIcon = icon_url || primary_url;
  const displayMessage = message || text;

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [duration]);

  useEffect(() => {
    if (isReward) {
      trackEvent(GA_EVENTS.coins_earned, {
        coins_earned: displayCoins,
        task_type: displayMessage
      });
    }
  }, []);

  const handleClose = (e) => {
    if (e) e.stopPropagation();
    setIsExiting(true);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const renderIcon = () => {
    if (icon) return <div className="snack-icon-wrapper">{icon}</div>;
    
    if (displayIcon) {
      return (
        <div className="snack-icon-wrapper relative flex items-center justify-center w-10">
          <img src={displayIcon} alt="reward" className="w-[18px] h-[18px] object-contain z-10" />
        </div>
      );
    }

    return null;
  };

  return (
    <SnackBarWrapper isExiting={isExiting}>
      <div 
        className={`
          flex items-center p-4 rounded-md shadow-[0_8px_32px_rgba(0,0,0,0.4)] backdrop-blur-xl border
          ${isReward 
            ? 'bg-gradient-to-r from-gray-900/95 to-black/95 border-yellow-500/30 shadow-[0_0_20px_rgba(234,179,8,0.1)]' 
            : 'bg-gray-900/90 text-white border-white/10'}
        `}
      >
        {renderIcon()}
        
        <div className="flex-1 min-w-0">
          {content ? (
            typeof content === 'function' ? content() : content
          ) : (
            <div className="flex flex-col gap-0.5">
              {isReward && action === 'reward_alert' && (
                <span className="text-[10px] uppercase tracking-wider text-yellow-500/80 font-bold"></span>
              )}
              <p className={`text-sm font-semibold truncate ${isReward ? 'text-white' : 'text-gray-100'}`}>
                {displayMessage}
              </p>
            </div>
          )}
        </div>

        <button 
          onClick={handleClose}
          className="ml-2 p-1.5 hover:bg-white/10 rounded-full transition-all group active:scale-90"
          aria-label="Close"
        >
          <svg className="w-4 h-4 text-gray-400 group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </SnackBarWrapper>
  );
};

export default SnackBar;
